import React, { useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Skeleton } from 'js/design_system/skeleton/Skeleton';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { trackClick } from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { getSearchUpsellTracking } from './searchUpsellTracking';
import { getSearchUpsellTracking as getExperimentTracking } from './searchUpsellExperiment';

type PreviewState =
  | 'loading'
  | 'personalized'
  | 'empty'
  | 'timeout'
  | 'rate_limited'
  | 'disabled'
  | 'error';

type Props = {
  query: string;
  url: string;
  trackExperiment?: boolean;
  previewUrl?: string;
};

const Container = styled(ImpressionWatcher).attrs({
  as: 'section',
  shouldTrackClick: (event: React.MouseEvent<HTMLElement>) =>
    event.target instanceof Element && event.target.closest('a') !== null,
})`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  margin-bottom: var(--spacing-sm-32, 32px);
  padding: var(--spacing-sm-24, 24px);
  border: 1px solid var(--background-beige-dark, #e2e0d9);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-beige-light, #f9f8f6);

  @include breakpoint(medium, large) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Copy = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const LoadingCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  flex: 1;
  width: 100%;
  min-height: 12.5rem;

  @include breakpoint(medium, large) {
    min-height: 4rem;
  }
`;

const Title = styled(Heading).attrs({ as: 'h2', size: 'sm' })`
  margin: 0;
`;

const Description = styled(Body).attrs({ size: 'sm' })`
  max-width: 720px;
  margin: 0;
  color: var(--text-beige-secondary, #585448);
`;

type SearchSummary = {
  returned_results: number;
  oldest_publication_year: number | null;
};

const PaperFinderCopy: React.FC<{
  query: string;
  summary?: SearchSummary;
}> = ({ query, summary }) => (
  <Copy>
    <Title>
      {summary ? (
        <>
          We found {summary.returned_results} closed-access{' '}
          {summary.returned_results === 1 ? 'paper' : 'papers'}
          {summary.oldest_publication_year
            ? ` published since ${summary.oldest_publication_year}`
            : ''}{' '}
          on &quot;{query}&quot;.
        </>
      ) : (
        'Closed access papers do not have to stay closed'
      )}
    </Title>
    <Description>
      {!summary && (
        <>
          Some recently published papers about &apos;{query}&apos; are closed
          access.{' '}
        </>
      )}
      Pick the ones you&apos;re interested in and we will try to get you the
      full text.
    </Description>
  </Copy>
);

export const PaperFinderUpsell: React.FC<Props> = ({
  query,
  url,
  trackExperiment = false,
  previewUrl,
}) => {
  const [preview, setPreview] = useState<{
    url: string;
    summary?: SearchSummary;
    state: PreviewState;
  }>();
  const lastTrackedState = useRef<string>();

  useEffect(() => {
    if (!previewUrl) return;

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      controller.abort();
      setPreview({ url: previewUrl, state: 'timeout' });
    }, 10_000);
    const loadPreview = async () => {
      let summary: SearchSummary | undefined;
      let state: PreviewState = 'error';
      try {
        const response = await fetch(previewUrl, { signal: controller.signal });
        if (response.status === 429) {
          state = 'rate_limited';
          return;
        }
        if (response.status === 204) {
          state = 'disabled';
          return;
        }
        if (!response.ok) return;

        const { meta }: { meta: SearchSummary } = await response.json();
        if (
          !Number.isInteger(meta?.returned_results) ||
          meta.returned_results < 0
        )
          return;
        state = meta.returned_results > 0 ? 'personalized' : 'empty';
        if (state === 'personalized') summary = meta;
      } catch {
        // Keep the existing offer if the optional search is unavailable.
      } finally {
        window.clearTimeout(timeout);
        if (!controller.signal.aborted) {
          setPreview({ url: previewUrl, summary, state });
        }
      }
    };
    void loadPreview();
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [previewUrl]);

  const loading = Boolean(previewUrl && preview?.url !== previewUrl);

  const previewState = loading ? 'loading' : preview?.state;
  useEffect(() => {
    if (!previewUrl || !previewState) return;
    const key = `${previewUrl}:${previewState}`;
    if (lastTrackedState.current === key) return;
    lastTrackedState.current = key;
    trackClick({
      source: 'paper_finder.preview_state',
      extra: { state: previewState },
    });
  }, [previewUrl, previewState]);

  return (
    <Container
      {...getSearchUpsellTracking(
        'paper_finder',
        'search_inline',
        'paper-finder-search-upsell',
      )}
      additionalTracking={
        trackExperiment
          ? {
              ...getExperimentTracking(
                'paper_finder',
                'search_inline',
                'paper-finder-search-upsell',
              ),
              noBatch: true,
            }
          : undefined
      }
    >
      {loading ? (
        <LoadingCopy>
          <Title>
            Searching for recent closed-access papers on &quot;{query}&quot;
          </Title>
          <Skeleton type="text" />
        </LoadingCopy>
      ) : (
        <>
          <PaperFinderCopy
            query={query}
            summary={preview?.url === previewUrl ? preview?.summary : undefined}
          />
          <ButtonLink href={url}>Find papers</ButtonLink>
        </>
      )}
    </Container>
  );
};

export const PaperFinderDashboardLink: React.FC<Props> = ({ query, url }) => (
  <Container source="paper_finder_dashboard_search_card" noBatch>
    <PaperFinderCopy query={query} />
    <ButtonLink href={url}>Open Paper Finder</ButtonLink>
  </Container>
);
